import { useState } from 'react';
import { Check } from '@phosphor-icons/react';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { TagMultiInput } from 'src/components/TagMultiInput';
import { useNotification } from 'src/contexts/NotificationContext';

interface CampaignSettingsProps {
  className?: string;
}

export const CampaignSettings = ({ className = '' }: CampaignSettingsProps) => {
  const notification = useNotification();

  const [notifications, setNotifications] = useState({
    weeklyDigest: true,
    callAnswerRateBelow: true,
    answerRateThreshold: 75,
    failureRateSpike: true,
  });

  const [notificationEmails, setNotificationEmails] = useState<string[]>([
    'exampleemail@address.com',
    'exampleemail2@address.com',
  ]);

  const handleNotificationChange = (key: keyof typeof notifications) => {
    setNotifications((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAnswerRateThresholdChange = (value: string) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      setNotifications((prev) => ({
        ...prev,
        answerRateThreshold: numValue,
      }));
    }
  };

  const emailValidator = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailError = (message: string) => {
    notification.error(message);
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Exit Criteria Section */}
      <div>
        <h2 className="text-2xl font-bold mb-2">Exit Criteria</h2>
        <p className="text-neutral-600 mb-6">
          See when contacts are automatically exited from this campaign
        </p>

        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Check
                className="text-green-600 flex-shrink-0"
                size={20}
                weight="bold"
              />
              <div className="flex-1">
                <Label className="text-sm font-medium text-neutral-700">
                  Contact answers a call from this campaign
                </Label>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Check
                className="text-green-600 flex-shrink-0"
                size={20}
                weight="bold"
              />
              <div className="flex-1">
                <Label className="text-sm font-medium text-neutral-700">
                  Contact explicitly requests to stop receiving communication
                  via any outreach channel
                </Label>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Check
                className="text-green-600 flex-shrink-0"
                size={20}
                weight="bold"
              />
              <div className="flex-1">
                <Label className="text-sm font-medium text-neutral-700">
                  Contact's number is unreachable
                </Label>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Divider TODO: hidden because we don't have notifications yet */}
      <div className="w-full border-t border-neutral-200 hidden"></div>

      {/* Notifications Section */}
      <div className="hidden">
        <h2 className="text-2xl font-bold mb-2">Notifications</h2>
        <p className="text-neutral-600 mb-6">
          Configure when you would like to receive email notifications related
          to this campaign
        </p>

        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="space-y-6">
            {/* Weekly Digest Checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                checked={notifications.weeklyDigest}
                onChange={() => handleNotificationChange('weeklyDigest')}
                ariaLabel="Receive a weekly digest"
              />
              <div
                className="flex-1"
                onClick={() => handleNotificationChange('weeklyDigest')}
              >
                <Label className="text-sm font-medium text-neutral-700">
                  Receive a weekly digest of all of the contacts that have
                  exited from this campaign
                </Label>
              </div>
            </div>

            {/* Call Answer Rate Checkbox with Input */}
            <div className="flex items-start gap-3">
              <Checkbox
                checked={notifications.callAnswerRateBelow}
                onChange={() => handleNotificationChange('callAnswerRateBelow')}
                ariaLabel="Notify when call answer rate drops below"
              />
              <div
                className="flex-1"
                onClick={() => handleNotificationChange('callAnswerRateBelow')}
              >
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-neutral-700">
                    Notify when call answer rate drops below
                  </Label>
                  <div className="flex items-center gap-1 relative top-[-5px]">
                    <Input
                      type="number"
                      value={notifications.answerRateThreshold}
                      onChange={(e) =>
                        handleAnswerRateThresholdChange(e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="w-16 text-center"
                      min="0"
                      max="100"
                      disabled={!notifications.callAnswerRateBelow}
                      size="small"
                    />
                    <span className="text-sm text-neutral-600">%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Failure Rate Spike Checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                checked={notifications.failureRateSpike}
                onChange={() => handleNotificationChange('failureRateSpike')}
                ariaLabel="Notify when failure or error rates spike"
              />
              <div
                className="flex-1"
                onClick={() => handleNotificationChange('failureRateSpike')}
              >
                <Label className="text-sm font-medium text-neutral-700">
                  Notify when failure or error rates spike
                </Label>
              </div>
            </div>

            {/* Email Notifications Input */}
            <div>
              <TagMultiInput
                label="Send email notifications to me and (use comma to separate):"
                values={notificationEmails}
                onChange={setNotificationEmails}
                placeholder="Add email address..."
                validator={emailValidator}
                errorMessage="Please enter a valid email address"
                onError={handleEmailError}
                required
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
